import { FC, useState } from 'react';
import { Modal, Loader, Stack, Text, Box } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up the worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export interface PDFViewerModalProps {
  opened: boolean;
  onClose: () => void;
  url: string | null;
  title?: string;
}

export const PDFViewerModal: FC<PDFViewerModalProps> = ({
  opened,
  onClose,
  url,
  title,
}) => {
  const { t } = useTranslation(['common']);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (): void => {
    setLoading(false);
    setError(
      t('pdfViewer.loadError', { defaultValue: 'Failed to load document' })
    );
  };

  const handleClose = (): void => {
    setNumPages(null);
    setLoading(true);
    setError(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={title || t('pdfViewer.title', { defaultValue: 'Document' })}
      size="90vw"
      centered
      styles={{
        content: {
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
        body: {
          flex: 1,
          padding: 0,
          overflow: 'hidden',
        },
      }}
    >
      {url ? (
        <Box
          style={{
            height: '100%',
            overflow: 'auto',
            backgroundColor: '#525659',
          }}
        >
          {loading && (
            <Stack align="center" justify="center" h="100%">
              <Loader size="lg" color="white" />
              <Text c="white">
                {t('pdfViewer.loading', {
                  defaultValue: 'Loading document...',
                })}
              </Text>
            </Stack>
          )}
          {error && (
            <Stack align="center" justify="center" h="100%">
              <Text c="red.3" ta="center">
                {error}
              </Text>
            </Stack>
          )}
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading=""
          >
            {numPages &&
              Array.from({ length: numPages }, (_, index) => (
                <Box
                  key={`page_${index + 1}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: 16,
                  }}
                >
                  <Page
                    pageNumber={index + 1}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    loading=""
                  />
                </Box>
              ))}
          </Document>
        </Box>
      ) : (
        <Stack align="center" justify="center" h="100%">
          <Loader size="lg" />
          <Text c="dimmed">
            {t('pdfViewer.loading', { defaultValue: 'Loading document...' })}
          </Text>
        </Stack>
      )}
    </Modal>
  );
};

export default PDFViewerModal;
